import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { TransHistory } from '../../services/types';
import { HeaderPagination } from '../ui';
import { parseCalendarDate } from '../../utils/calendarDate';

interface PartTransHistTableProps {
  transHist: TransHistory[];
  currentPage: number;
  pageSize: number;
  morePages: boolean;
  isLoading?: boolean;
  onPreviousPage?: () => void;
  onNextPage?: () => void;
}

export const PartTransHistTable: React.FC<PartTransHistTableProps> = ({
  transHist,
  currentPage,
  pageSize,
  morePages,
  isLoading = false,
  onPreviousPage,
  onNextPage,
}) => {
  const { t } = useTranslation();

  const totalQty = transHist.reduce((sum, record) => sum + record.tranQty, 0);
  const showingFrom = (currentPage - 1) * pageSize + 1;
  const showingTo = showingFrom + transHist.length - 1;

  const canGoPrevious = currentPage > 1 && !isLoading;
  const canGoNext = morePages && !isLoading;

  return (
    <div className="bg-white rounded border border-gray-300 shadow-sm flex flex-col">
      {/* Header with integrated pagination */}
      <div className="bg-indigo-50 px-4 py-3 border-b border-indigo-200 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-indigo-900">
              {t('parts.transactionHistory')}
            </h3>
            <p className="text-xs text-indigo-700">
              {transHist.length > 0
                ? `${showingFrom}-${showingTo}`
                : transHist.length}{' '}
              {transHist.length === 1
                ? t('parts.records')
                : t('parts.recordPlural')}
              {totalQty !== 0 && ` • Total: ${totalQty.toLocaleString()}`}
            </p>
          </div>

          {/* Pagination controls in header */}
          {transHist.length > 0 && (
            <HeaderPagination
              currentPage={currentPage}
              canGoPrevious={canGoPrevious}
              canGoNext={canGoNext}
              onPreviousPage={onPreviousPage}
              onNextPage={onNextPage}
              colorScheme="indigo"
            />
          )}
        </div>
      </div>

      {/* Content */}
      {transHist.length > 0 ? (
        <div className="overflow-x-auto flex-grow">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-200 sticky top-0">
                <th className="px-4 py-3 text-left font-semibold text-gray-700">
                  {t('parts.transactionHistoryTable.date')}
                </th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">
                  {t('parts.transactionHistoryTable.qty')}
                </th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">
                  {t('parts.transactionHistoryTable.cumulative', 'Cumulative')}
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">
                  {t('parts.transactionHistoryTable.details')}
                </th>
              </tr>
            </thead>
            <tbody className={isLoading ? 'opacity-50' : ''}>
              {transHist.map((record, idx) => (
                <tr key={idx} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-gray-900 font-medium whitespace-nowrap">
                    {formatDate(record.tranDate)}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-900 font-semibold">
                    {record.tranQty.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700">
                    {record.runningTotal.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-gray-700 text-xs">
                    {renderTextWithJobLinks(record.humanReadable)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="p-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="pb-2">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-tight mb-1">
                Date
              </p>
              <p className="text-sm font-medium text-gray-900">-</p>
            </div>
            <div className="pb-2 text-right">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-tight mb-1">
                Qty
              </p>
              <p className="text-sm font-medium text-gray-900">0</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Render text with clickable links for job, order and PO numbers
 * Converts "Job XXXXX" patterns into links to /job-tracker?jobNum=XXXXX
 * Converts "Order XXXXX" patterns into links to /sales-orders/XXXXX
 * Converts "PO XXXXX" patterns (e.g. "Purchased to Stock (PO 601000)") into links to /purchase-orders/XXXXX
 */
function renderTextWithJobLinks(text: string): React.ReactNode {
  if (!text) return text;

  // Match "Job", "Order" or "PO" followed by spaces and digits
  const linkPattern = /(Job|Order|\bPO)\s+(\d+)/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = linkPattern.exec(text)) !== null) {
    const type = match[1]; // "Job", "Order" or "PO"
    const num = match[2]; // The digits
    const matchStart = match.index;
    const matchEnd = linkPattern.lastIndex;

    // Add text before the match
    if (matchStart > lastIndex) {
      parts.push(text.substring(lastIndex, matchStart));
    }

    // Determine the link destination based on type
    const linkTo =
      type === 'Job'
        ? `/job-tracker?jobNum=${num}`
        : type === 'PO'
          ? `/purchase-orders/${num}`
          : `/sales-orders/${num}`;

    // Add link for the job, order or PO number
    parts.push(
      <Link
        key={matchStart}
        to={linkTo}
        className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
      >
        {type} {num}
      </Link>
    );

    lastIndex = matchEnd;
  }

  // Add any remaining text after the last match
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  // If no matches found, return original text
  return parts.length > 0 ? parts : text;
}

/**
 * Format date string for display
 * Handles ISO format, timestamps, and various date formats
 */
function formatDate(dateString: string): string {
  if (!dateString) return '-';

  try {
    let date: Date;

    // Try parsing as ISO date first
    if (dateString.includes('T') || dateString.includes('-')) {
      date = parseCalendarDate(dateString);
    } else if (/^\d+$/.test(dateString)) {
      // Handle timestamp in milliseconds
      date = new Date(parseInt(dateString, 10));
    } else {
      // Try direct Date constructor
      date = new Date(dateString);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return dateString; // Return original if parsing failed
    }

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
}
